import { z } from 'zod';

// T-215 (collettiva): testata della "scheda di produzione giornaliera"
// (batch record BRCGS 9 §3.9 / IFS 8 §4.18, registrazione HACCP ex Reg.
// 852/2004 art. 5). Una riga per (tenant, sede, data). Il corpo della scheda
// deriva dalle `productions` del giorno (lotti+quantità per ingrediente) e
// dagli `shipments` emessi (destinazioni). Qui vivono i dati di giornata:
// pasto campione (clausola capitolati: >=150 g, +4 °C, 72 h), totale pasti,
// chiusura (operatore) e verifica di secondo livello (BRCGS 2.10.2).
// T-349: include 'colazione' — gli ospedali della collettiva servono anche la
// colazione; il vincolo DB su productions.meal_service è allineato (migration
// 20260702090000_productions_meal_colazione.sql).
export const MEAL_SERVICES = ['colazione', 'pranzo', 'cena'] as const;
export const MealServiceSchema = z.enum(MEAL_SERVICES);
export type MealService = z.infer<typeof MealServiceSchema>;

// Id DETERMINISTICO per la scheda del giorno: SHA-256(tenant|business|data)
// piegato a UUID (variante RFC 4122). Due device offline che creano la stessa
// giornata generano lo STESSO id → il secondo insert è un replay idempotente
// sulla PK (accettato da sync-push) invece di una violazione della UNIQUE
// (tenant,business,sheet_date) che scarterebbe in silenzio la scheda del
// secondo device. I campi si riconciliano poi via OCC normale.
export async function productionDayIdFor(
  tenantId: string,
  businessId: string,
  sheetDate: string,
): Promise<string> {
  const data = new TextEncoder().encode(`production_day|${tenantId}|${businessId}|${sheetDate}`);
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', data));
  const b = digest.slice(0, 16);
  b[6] = (b[6] & 0x0f) | 0x50; // name-based (v5-like)
  b[8] = (b[8] & 0x3f) | 0x80; // variante RFC 4122
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export const ProductionDaySchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  business_id: z.string().uuid(),
  sheet_date: z.string().date(),
  sample_meal_taken: z.boolean().default(false),
  sample_meal_at: z.string().datetime().nullable(),
  sample_meal_notes: z.string().nullable(),
  meals_total: z.number().int().nonnegative().nullable(),
  meals_notes: z.string().nullable(),
  notes: z.string().nullable(),
  closed_at: z.string().datetime().nullable(),
  closed_by: z.string().uuid().nullable(),
  verified_by: z.string().uuid().nullable(),
  verified_at: z.string().datetime().nullable(),
  reopen_count: z.number().int().nonnegative().default(0),
  reopened_at: z.string().datetime().nullable().default(null),
  version: z.number().int().nonnegative().default(0),
  created_at: z.string().datetime(),
  archived_at: z.string().datetime().nullable(),
});
export type ProductionDay = z.infer<typeof ProductionDaySchema>;
